import React from 'react';
import Swal from 'sweetalert2';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { observer } from 'mobx-react-lite';
import {
   Badge,
   Box,
   Button,
   Flex,
   IconButton,
   ScrollArea,
   Select,
   Spinner,
   Table,
   Tabs,
   Text,
   Tooltip,
} from '@radix-ui/themes';

import { DateUtilsService } from 'shared/utils/date-utils.service';
import {
   CheckIcon,
   EmailIcon,
   IconEast,
   IconWest,
   NotInterestedIcon,
   RemoveRedEyeIcon,
} from 'shared/icons';
import { JobsBrowserManagerVacanciesStore } from './jobs-browser.store';
import { JobsBrowserListItem, JobsBrowserSort, JobsBrowserStatus } from './jobs-browser.types';

type JobsBrowserVacanciesProps = {
   store: JobsBrowserManagerVacanciesStore;
   searchQuery: string;
   onError: (message: string) => void;
   onAuthError: () => void;
};

type VacanciesTableProps = {
   store: JobsBrowserManagerVacanciesStore;
   onSortChange: (value: string) => void;
   onPrev: () => void;
   onNext: () => void;
   onJobStatusChange: (item: JobsBrowserListItem) => void;
};

const ACTIVE_TAB_LABEL = '\u0410\u043A\u0442\u0438\u0432\u043D\u0456';
const ARCHIVED_TAB_LABEL = '\u0410\u0440\u0445\u0456\u0432\u043D\u0456';

const scrollToManager = (managerId: number) => {
   const element = document.getElementById(`jobs-browser-manager-${managerId}`);

   if (!element) {
      return;
   }

   const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;

   window.scrollTo({
      top: elementPosition - 90,
      behavior: 'smooth',
   });
};

const renderLocation = (job: JobsBrowserListItem['Job']) => {
   return job.location || job.job_city || job.address || '-';
};

const renderSortLabel = (sort: JobsBrowserSort, t: (key: string) => string) => {
   if (sort === 'apply') {
      return t('employerManageJobs.jobseeker');
   }

   return t('employerManageJobs.postedOn');
};

const toStatusTabValue = (status: JobsBrowserStatus) => String(status);

function JobsBrowserVacanciesView({
   store,
   searchQuery,
   onError,
   onAuthError,
}: JobsBrowserVacanciesProps) {
   const { t } = useTranslation('global');

   const handleToggleExpanded = async () => {
      await store.toggleExpanded(searchQuery, { onError, onAuthError });
   };

   const handleSortChange = async (value: string) => {
      await store.setSort(value as JobsBrowserSort, searchQuery, { onError, onAuthError });
   };

   const handleStatusChange = async (value: string) => {
      await store.setStatus(Number(value) as JobsBrowserStatus, searchQuery, {
         onError,
         onAuthError,
      });
   };

   const handlePrev = async () => {
      await store.onPrev(searchQuery, { onError, onAuthError });
      scrollToManager(store.manager.id);
   };

   const handleNext = async () => {
      await store.onNext(searchQuery, { onError, onAuthError });
      scrollToManager(store.manager.id);
   };

   const handleJobStatusChange = async (item: JobsBrowserListItem) => {
      const active = item.Job.status === 1;
      const confirmationResult = await Swal.fire({
         icon: 'question',
         showCancelButton: true,
         cancelButtonText: t('employerManageJobs.no'),
         confirmButtonText: t('employerManageJobs.yes'),
         text: active
            ? t('employerManageJobs.deactivateConfirmTxt')
            : t('employerManageJobs.activateConfirmTxt'),
         title: active
            ? t('employerManageJobs.deactivateConfirmTitle')
            : t('employerManageJobs.activateConfirmTitle'),
      });

      if (!confirmationResult.isConfirmed) {
         return;
      }

      const callbacks = {
         searchQuery,
         slug: item.Job.slug,
         onAuthError,
         onError,
         onSuccess: () => {
            Swal.fire({
               icon: 'success',
               confirmButtonText: t('employerManageJobs.close'),
               title: active
                  ? t('employerManageJobs.deactivateSuccessTitle')
                  : t('employerManageJobs.activateSuccessTitle'),
            });
         },
      };

      if (active) {
         await store.deactivateJob(callbacks);
         return;
      }

      await store.activateJob(callbacks);
   };

   if (!store.hasVacancies) {
      return <p className="mt-3 text-muted">{t('employerManageJobs.noVacancies')}</p>;
   }

   return (
      <Box mt="3">
         <Button
            className="jobsBrowserToggleButton"
            type="button"
            size="2"
            variant="soft"
            color="blue"
            highContrast
            onClick={handleToggleExpanded}
         >
            {store.expanded
               ? t('employerManageJobs.hideVacancies')
               : `${t('employerManageJobs.showVacancies')} - ${store.totalVacanciesCount}`}
         </Button>

         {store.expanded ? (
            <Box mt="4">
               <Tabs.Root
                  className="jobsBrowserStatusTabs"
                  value={toStatusTabValue(store.status)}
                  onValueChange={handleStatusChange}
               >
                  <Tabs.List className="jobsBrowserStatusTabsList" size="2">
                     <Tabs.Trigger value={toStatusTabValue(JobsBrowserStatus.ACTIVE)}>
                        {ACTIVE_TAB_LABEL} ({store.activeVacanciesCount})
                     </Tabs.Trigger>
                     <Tabs.Trigger value={toStatusTabValue(JobsBrowserStatus.ARCHIVED)}>
                        {ARCHIVED_TAB_LABEL} ({store.archivedVacanciesCount})
                     </Tabs.Trigger>
                  </Tabs.List>

                  <Box pt="3">
                     <Tabs.Content value={toStatusTabValue(JobsBrowserStatus.ACTIVE)}>
                        <VacanciesTable
                           store={store}
                           onSortChange={handleSortChange}
                           onPrev={handlePrev}
                           onNext={handleNext}
                           onJobStatusChange={handleJobStatusChange}
                        />
                     </Tabs.Content>
                     <Tabs.Content value={toStatusTabValue(JobsBrowserStatus.ARCHIVED)}>
                        <VacanciesTable
                           store={store}
                           onSortChange={handleSortChange}
                           onPrev={handlePrev}
                           onNext={handleNext}
                           onJobStatusChange={handleJobStatusChange}
                        />
                     </Tabs.Content>
                  </Box>
               </Tabs.Root>
            </Box>
         ) : null}
      </Box>
   );
}

const VacanciesTable = observer(
   ({ store, onSortChange, onPrev, onNext, onJobStatusChange }: VacanciesTableProps) => {
      const { t } = useTranslation('global');

      return (
         <>
            <Flex justify="between" align="center" gap="3" mb="3" wrap="wrap">
               <Text className="jobsBrowserCountText" size="2" color="gray">
                  {t('employerManageJobs.jobsCount')} {store.recordStart} - {store.recordEnd}{' '}
                  {t('pagination.of')} {store.totalCount}
               </Text>

               <Select.Root value={store.sort} onValueChange={onSortChange}>
                  <Select.Trigger
                     className="jobsBrowserSortTrigger"
                     aria-label="Sort vacancies"
                  />
                  <Select.Content position="popper">
                     <Select.Item value="date">{renderSortLabel('date', t)}</Select.Item>
                     <Select.Item value="apply">{renderSortLabel('apply', t)}</Select.Item>
                  </Select.Content>
               </Select.Root>
            </Flex>

            {store.loading ? (
               <Flex justify="center" py="5">
                  <Spinner size="2" />
               </Flex>
            ) : (
               <ScrollArea type="auto">
                  <Table.Root className="jobsBrowserVacanciesTable" size="2" variant="surface">
                     <Table.Header>
                        <Table.Row>
                           <Table.ColumnHeaderCell>
                              {t('employerManageJobs.jobs')}
                           </Table.ColumnHeaderCell>
                           <Table.ColumnHeaderCell>
                              {t('employerManageJobs.vacancyID')}
                           </Table.ColumnHeaderCell>
                           <Table.ColumnHeaderCell>
                              {t('employerManageJobs.location')}
                           </Table.ColumnHeaderCell>
                           <Table.ColumnHeaderCell>
                              {t('employerManageJobs.postedOn')}
                           </Table.ColumnHeaderCell>
                           <Table.ColumnHeaderCell>
                              <Tooltip content={t('employerManageJobs.views')}>
                                 <Flex>
                                    <RemoveRedEyeIcon width={20} height={20} />
                                 </Flex>
                              </Tooltip>
                           </Table.ColumnHeaderCell>
                           <Table.ColumnHeaderCell>
                              {t('employerManageJobs.jobseeker')}
                           </Table.ColumnHeaderCell>
                           <Table.ColumnHeaderCell>
                              <Tooltip content={t('employerManageJobs.notifiedJobseekers')}>
                                 <Flex>
                                    <EmailIcon width={20} height={20} />
                                 </Flex>
                              </Tooltip>
                           </Table.ColumnHeaderCell>
                           <Table.ColumnHeaderCell>
                              {t('employerManageJobs.status')}
                           </Table.ColumnHeaderCell>
                        </Table.Row>
                     </Table.Header>

                     <Table.Body>
                        {store.vacancies.map((item) => (
                           <Table.Row key={item.Job.id}>
                              <Table.Cell>
                                 <Link to={`/user/managejob/accdetail/${item.Job.slug}`}>
                                    <Text className="jobsBrowserJobTitle" weight="medium">
                                       {item.Job.title}
                                    </Text>
                                    <Text className="jobsBrowserPlanDetails" as="div" color="gray" mt="1">
                                       {item.planDetails}
                                    </Text>
                                 </Link>
                              </Table.Cell>
                              <Table.Cell>{item.Job.id}</Table.Cell>
                              <Table.Cell>{renderLocation(item.Job)}</Table.Cell>
                              <Table.Cell>{DateUtilsService.toAppFormat(item.Job.created)}</Table.Cell>
                              <Table.Cell>{item.Job.view_count}</Table.Cell>
                              <Table.Cell>
                                 <Link to={`/user/managejob/accdetail/${item.Job.slug}`}>
                                    {t('employerManageJobs.total')}: {item.totalCandidate}
                                 </Link>
                                 <Text className="jobsBrowserApplicationNew" as="div" color="gray">
                                    {t('employerManageJobs.new')}: {item.newApplicationCount}
                                 </Text>
                              </Table.Cell>
                              <Table.Cell>{item.jobAlert}</Table.Cell>
                              <Table.Cell>
                                 <Tooltip
                                    content={
                                       item.Job.status === 1
                                          ? t('employerManageJobs.deactivateConfirmTitle')
                                          : t('employerManageJobs.activateConfirmTitle')
                                    }
                                 >
                                    <IconButton
                                       type="button"
                                       color={item.Job.status === 1 ? 'green' : 'gray'}
                                       variant="soft"
                                       disabled={store.actionLoading}
                                       onClick={() => {
                                          onJobStatusChange(item);
                                       }}
                                    >
                                       {item.Job.status === 1 ? (
                                          <CheckIcon width={18} height={18} />
                                       ) : (
                                          <NotInterestedIcon width={18} height={18} />
                                       )}
                                    </IconButton>
                                 </Tooltip>
                              </Table.Cell>
                           </Table.Row>
                        ))}
                     </Table.Body>
                  </Table.Root>
               </ScrollArea>
            )}

            <Flex justify="between" align="center" gap="3" mt="3" wrap="wrap">
                  <Badge className="jobsBrowserCountBadge" color="gray" variant="soft">
                  {t('employerManageJobs.jobsCount')} {store.recordStart} - {store.recordEnd}{' '}
                  {t('pagination.of')} {store.totalCount}
               </Badge>

               <Flex justify="center" gap="2">
                  <Button
                     className="jobsBrowserPaginationButton"
                     type="button"
                     size="2"
                     variant="outline"
                     color="red"
                     disabled={!store.canPrev}
                     onClick={onPrev}
                  >
                     <IconWest width={16} height={16} />
                     {t('pagination.Prev')}
                  </Button>
                  <Button
                     className="jobsBrowserPaginationButton"
                     type="button"
                     size="2"
                     variant="outline"
                     color="red"
                     disabled={!store.canNext}
                     onClick={onNext}
                  >
                     {t('pagination.Next')}
                     <IconEast width={16} height={16} />
                  </Button>
               </Flex>
            </Flex>
         </>
      );
   }
);

export const JobsBrowserVacancies = observer(JobsBrowserVacanciesView);
